import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CAMPAIGN_STAGES, ROLES, TASK_PRIORITY, CAMPAIGN_STUDIO_STATUS } from '../../../../core/helpers/constants';
import { DataFeedService } from '../../../../shared/api/datafeed.service';
import { AppService } from '../../../../store/app.service';
import { SearchesApiService } from '../../searches.apiservice';
import { InstanceService } from '../../../../core/instance/instance.service';

@Component({
  selector: 'app-new-search-main',
  templateUrl: './new-search-main.component.html',
  styleUrls: ['./new-search-main.component.scss']
})
export class NewSearchMainComponent implements OnInit {
  @Output() searchSaved = new EventEmitter();

  active = 1;
  newSearchForm!: FormGroup;
  submitted = false;

  campaignDisabled = false;
  companyDisabled = false;
  clientDisabled = false;
  userDisabled = false;
  tagDisabled = false;
  leadDisabled = false;
  taskDisabled = false;
  prospectDisabled = false;

  companyVisible = true;
  clientVisible = true;
  campaignVisible = true;
  userVisible = true;
  tagVisible = true;
  leadVisible = true;
  taskVisible = true;
  prospectVisible = true;

  editSavedSearchId!: string;
  allowedTypes: any[] = [];
  public allResultTypesPages = ['Campaigns', 'Clients', 'Leads', 'Users', 'Companies', 'Prospects', 'Tags', 'Tasks'];
  public allResultTypes = ['Campaign', 'Client', 'Lead', 'User', 'Company', 'Prospect', 'Tag', 'Task'];

  public resultTypeLabelKeyMap: Record<string, string> = {
    Campaign: 'CAMPAIGN',
    Client: 'CLIENT',
    Lead: 'LEAD',
    User: 'USER',
    Company: 'COMPANY',
    Prospect: 'PROSPECT',
    Tag: 'TAG',
    Task: 'TASK'
  };
  public stringOperations = [
    { name: 'OPERATORS.EQUALS', value: 'Equals' },
    { name: 'OPERATORS.NOT_EQUALS', value: 'NotEquals' },
    { name: 'OPERATORS.STARTS_WITH', value: 'StartsWith' },
    { name: 'OPERATORS.ENDS_WITH', value: 'EndsWith' },
    { name: 'OPERATORS.CONTAINS', value: 'Contains' },
    { name: 'OPERATORS.DOESNT_CONTAIN', value: 'DoesntContain' },
    { name: 'OPERATORS.IS_EMPTY', value: 'IsEmpty' },
    { name: 'OPERATORS.IS_FILLED', value: 'IsFilled' }];

  public defaultStringOperation = 'Contains';

  constructor(
    private formBuilder: FormBuilder,
    private appService: AppService,
    private router: Router,
    private route: ActivatedRoute,
    private searchApi: SearchesApiService,
    private instance: InstanceService,
    private dataFeedApi: DataFeedService) {

    this.newSearchForm = this.formBuilder.group({
      title: ['', [Validators.required]],
      resultType: ['Campaign', [Validators.required]],
    });
    this.onResultTypeChange(this.allResultTypes[0]);

    const userRoles = this.appService.getActiveUserRoles$().value;
    if (userRoles.find(x => x.code == ROLES.Admin.Code))
      this.allowedTypes == this.allResultTypesPages;
    else {
      for (var r of userRoles) {
        for (var p of r.perms) {
          this.allowedTypes.push(p.pageName);
        }
      }
      for (let i = this.allResultTypes.length - 1; i >= 0; i--) {
        let page = this.allResultTypesPages[i];
        let found = this.allowedTypes.find((x: any) => page === x) !== undefined;
        if (!found) {
          this.allResultTypes.splice(i, 1);
          if (i === 7)
            this.taskVisible = false;
          if (i === 6)
            this.tagVisible = false;
          if (i === 5)
            this.prospectVisible = false;
          if (i === 4)
            this.companyVisible = false;
          if (i === 3)
            this.userVisible = false;
          if (i === 2)
            this.leadVisible = false;
          if (i === 1)
            this.clientVisible = false;
          if (i === 0)
            this.campaignVisible = false;
        }
      }
    }
    this.prospectColumns = this.clientsColumns.map(x => {
      var newControlName: any = `prospects.${x.formControlName.split('.')[1]}`;
      return { ...x, formControlName: newControlName }
    })
    this.dataFeedApi.getAllTaskAssignees()
      .subscribe((response: any) => {
        if (response.good) {
          var index = this.taskColumns.findIndex(x => x.formControlName === 'tasks.assignedUser');
          this.taskColumns[index].value.options = response.data.map((x: any) => {
            return { name: x.firstName + ' ' + x.lastName, value: x.id }
          });
          this.taskColumns[index].value.options?.splice(0, 0, { name: 'ALL', value: '' })
          this.taskColumns = [...this.taskColumns];
        }
      });
  }
  get form() { return this.newSearchForm.controls; }

  ngOnInit(): void {
    this.applyCampaignColumnsForLayout();
    this.applyLeadColumnsForNameOrder();

    const savedSearchId = this.route.snapshot.paramMap.get('id');
    if (savedSearchId) {
      this.editSavedSearchId = savedSearchId;
      this.editSavedSearch(savedSearchId);
    }
  }

  private applyLeadColumnsForNameOrder() {
    const first = this.leadColumns.find((c: any) => c?.formControlName === 'lead.firstName');
    const last = this.leadColumns.find((c: any) => c?.formControlName === 'lead.lastName');
    if (!first || !last) return;

    const rest = this.leadColumns.filter(
      (c: any) => c?.formControlName !== 'lead.firstName' && c?.formControlName !== 'lead.lastName'
    );

    this.leadColumns = this.instance.isLastNameFirst ? [last, first, ...rest] : [first, last, ...rest];
  }

  private applyCampaignColumnsForLayout() {
    if (!this.instance.isAltLayout) return;
    const hidden = new Set([
      'campaign.locationEmail',
      'campaign.street',
      'campaign.city',
      'campaign.state',
      'campaign.zipcode',
      'campaign.studioStatus',
      'campaign.campaignNotes',
      'campaign.campaignCreationNotes',
      'campaign.crmName',
      'campaign.crmId'
    ]);

    this.campaignColumns = this.campaignColumns.filter((c: any) => !hidden.has(c.formControlName));

    const hiddenClient = new Set([
      'client.salesRep',
      'client.notes',
      'client.firstName'
    ]);
    this.clientsColumns = this.clientsColumns.filter((c: any) => !hiddenClient.has(c.formControlName));

    // Alt layout only supports searching by a single client name field; display it as "Name".
    this.clientsColumns = this.clientsColumns.map((c: any) => {
      if (c?.formControlName === 'client.lastName' && c?.name === 'LAST_NAME') {
        return { ...c, name: 'NAME' };
      }
      return c;
    });

    // Keep prospect columns consistent with the adjusted client columns.
    this.prospectColumns = this.clientsColumns.map((x: any) => {
      const newControlName: any = `prospects.${x.formControlName.split('.')[1]}`;
      return { ...x, formControlName: newControlName };
    });

    // Hide Task tab + remove Task from the result type dropdown for alt layout
    this.taskVisible = false;
    const taskIndex = this.allResultTypes.findIndex(x => x === 'Task');
    if (taskIndex >= 0) this.allResultTypes.splice(taskIndex, 1);
    const tasksPageIndex = this.allResultTypesPages.findIndex(x => x === 'Tasks');
    if (tasksPageIndex >= 0) this.allResultTypesPages.splice(tasksPageIndex, 1);

    const currentResultType = this.newSearchForm?.controls?.['resultType']?.value;
    if (currentResultType === 'Task') {
      const fallbackType = this.allResultTypes[0] ?? 'Campaign';
      this.newSearchForm.controls['resultType'].setValue(fallbackType);
      this.onResultTypeChange(fallbackType);
    }
  }

  editSavedSearch(savedSearchId: string) {
    const that = this;
    this.searchApi.getSavedSearch(savedSearchId)
      .subscribe((response: any) => {
        if (response.good) {
          // console.log(response);
          that.newSearchForm.controls['title'].setValue(response.savedSearch.name);
          var resultType = response.savedSearch.resultType.charAt(0).toUpperCase() + response.savedSearch.resultType.slice(1);
          if (this.instance.isAltLayout && resultType === 'Task') {
            resultType = this.allResultTypes[0] ?? 'Campaign';
          }
          that.newSearchForm.controls['resultType'].setValue(resultType);
          this.onResultTypeChange(resultType);
          for (var i = 0; i < response.savedSearch.fields.length; i++) {
            const field = response.savedSearch.fields[i];
            if (field.name.indexOf('_') >= 0) {
              const name = field.dataType + "." + field.name.substring(0, field.name.indexOf('_'));
              var columns = this.getColumnsByType(field.dataType) ?? [];
              const index = columns.findIndex((x: any) => x.formControlName === name);
              if (index < 0 || !columns[index]) {
                continue;
              }
              const newField: any = JSON.parse(JSON.stringify(columns[index]));
              newField.formControlName = field.dataType + "." + field.name;
              newField.operator.default = field.operator;
              newField.value.defaultValue = field.value;
              newField.deletable = true;
              this.addNewColumn(field.dataType, newField);
            }
            else {
              const opControl = that.newSearchForm.controls[`${field.dataType}.${field.name}Operation`];
              const valueControl = that.newSearchForm.controls[`${field.dataType}.${field.name}`];
              if (opControl) {
                opControl.setValue(field.operator);
              }
              if (valueControl) {
                valueControl.setValue(field.value);
              }
            }
          }
        }
      });
  }

  onResultTypeChange(newResultType: any) {
    this.active = this.allResultTypes.findIndex(x => x === newResultType) + 1;
    if (newResultType === 'Campaign') {
      this.campaignDisabled = this.clientDisabled = this.userDisabled = this.tagDisabled = false;
      this.companyDisabled = this.leadDisabled = this.taskDisabled = this.prospectDisabled = true;
    } else if (newResultType === 'Company') {
      this.companyDisabled = this.clientDisabled = this.userDisabled = false;
      this.campaignDisabled = this.leadDisabled = this.tagDisabled = this.taskDisabled = this.prospectDisabled = true;
    } else if (newResultType === 'Client') {
      this.clientDisabled = this.companyDisabled = this.campaignDisabled = this.userDisabled = this.tagDisabled = false;
      this.leadDisabled = this.taskDisabled = this.prospectDisabled = true;
    } else if (newResultType === 'Prospect') {
      this.prospectDisabled = false;
      this.clientDisabled = this.leadDisabled = this.companyDisabled = this.campaignDisabled = this.userDisabled = this.tagDisabled = this.taskDisabled = true;
    } else if (newResultType === 'User') {
      this.userDisabled = false;
      this.companyDisabled = this.clientDisabled = this.tagDisabled = this.campaignDisabled = this.leadDisabled = this.taskDisabled = this.prospectDisabled = true;
    } else if (newResultType === 'Tag') {
      this.tagDisabled = false;
      this.companyDisabled = this.clientDisabled = this.campaignDisabled = this.userDisabled = this.leadDisabled = this.taskDisabled = this.prospectDisabled = true;
    } else if (newResultType === 'Lead') {
      this.leadDisabled = this.campaignDisabled = false;
      this.companyDisabled = this.clientDisabled = this.tagDisabled = this.userDisabled = this.taskDisabled = this.prospectDisabled = true;
    }
    else if (newResultType === 'Task') {
      this.taskDisabled = this.campaignDisabled = this.clientDisabled = this.userDisabled = false;
      this.companyDisabled = this.leadDisabled = this.tagDisabled = this.prospectDisabled = true;
    }
  }

  saveClicked() {
    this.submitted = true;
    if (!this.newSearchForm.valid) return;
    var search: any = { id: this.editSavedSearchId };
    search.fields = [];
    for (var key of Object.keys(this.newSearchForm.value)) {
      if (key === 'title')
        search.name = this.newSearchForm.value[key];
      else if (key === 'resultType')
        search.resultType = this.newSearchForm.value[key];
      else if (key.endsWith('Operation'))
        continue;
      else {
        var value = this.newSearchForm.value[key];
        var operator = this.newSearchForm.value[key + 'Operation'];
        if ((value === '' || value === undefined) && operator !== 'IsEmpty' && operator !== 'IsFilled')
          continue;
        var dataType = key.split('.')[0];
        var name = key.split('.')[1];
        search.fields.push({
          dataType,
          name,
          operator,
          value
        });
      }
    }
    this.appService.addSearch$(search);
    this.resetForm();
    if (this.editSavedSearchId) {
      this.router.navigate(['/pages/searches/']);
    }
    else {
      this.searchSaved.emit();
    }
  }

  resetForm() {
    this.newSearchForm.controls['title'].setValue('');
    this.newSearchForm.controls['resultType'].setValue('Campaign');
  }

  cancelClicked() {
    this.reset();
    this.router.navigate(['/pages/searches/']);
    this.searchSaved.emit();
  }

  reset() {
    this.campaignDisabled = this.clientDisabled = this.userDisabled = this.tagDisabled = this.companyDisabled = this.taskDisabled = true;
    this.resetForm();
  }

  onCompletionDateChange(value: any, row: any) {
    var completedWithinDatePickerEnabled = false;
    if (value === '') { // anytime
      this.newSearchForm.controls[row.formControlName].patchValue('');
    } else if (value === 'Today') { // today
      this.newSearchForm.controls[row.formControlName].patchValue(this.getDateString(0));
    } else if (value === 'LastWeek') { // last week
      this.newSearchForm.controls[row.formControlName].patchValue(this.getDateString(-7));
    } else if (value === 'LastMonth') { // last month
      this.newSearchForm.controls[row.formControlName].patchValue(this.getDateString(-30));
    } else if (value === 'Last90Days') { // 90 days
      this.newSearchForm.controls[row.formControlName].patchValue(this.getDateString(-90));
    } else if (value === 'CustomPeriod') { // custom
      this.newSearchForm.controls[row.formControlName].patchValue('');
      completedWithinDatePickerEnabled = true;
    }
    var index = this.taskColumns.findIndex(x => x.formControlName === row.formControlName)
    this.taskColumns[index].value.clickOpens = completedWithinDatePickerEnabled;
    this.taskColumns = [...this.taskColumns];
  }

  getDateString(days: number) {
    var today = new Date();
    var target = new Date(today.valueOf());
    target.setDate(today.getDate() + days);
    var dateToString = (date: Date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    if (days >= 0) {
      return `${dateToString(today)} to ${dateToString(target)}`;
    } else {
      return `${dateToString(target)} to ${dateToString(today)}`;
    }
  }

  campaignStages = [{ name: '', value: '' }, ...CAMPAIGN_STAGES.map(x => { return { name: x.translate ?? x.title, value: x.value.toString() } })];
  campaignStudioStatus = [{ name: '', value: '' }, ...CAMPAIGN_STUDIO_STATUS.map(x => {
    const key = `CAMPAIGN_STUDIO_STATUS.${(x.title ?? '').replace(/\s+/g, '_').toUpperCase()}`;
    return { name: key, value: x.value.toString() };
  })];

  campaignColumns = [
    {
      name: 'NAME',
      formControlName: 'campaign.name',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PLATFORM',
      formControlName: 'campaign.platform',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LOCATION_EMAIL',
      formControlName: 'campaign.locationEmail',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LOCATION_STREET',
      formControlName: 'campaign.street',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LOCATION_CITY',
      formControlName: 'campaign.city',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LOCATION_STATE',
      formControlName: 'campaign.state',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LOCATION_ZIPCODE',
      formControlName: 'campaign.zipcode',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'STUDIO_STATUS',
      formControlName: 'campaign.studioStatus',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }, { name: 'OPERATORS.NOT_EQUALS', value: 'Not Equals' }], default: 'Equals' },
      value: { type: 'select', options: this.campaignStudioStatus }
    },
    {
      name: 'PLATFORM_CAMPAIGN_ID',
      formControlName: 'campaign.platformCampaignId',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PLATFORM_ACCOUNT_ID',
      formControlName: 'campaign.platformAccountId',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'DMS',
      formControlName: 'campaign.dms',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'STAGE',
      formControlName: 'campaign.stage',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }, { name: 'OPERATORS.NOT_EQUALS', value: 'Not Equals' }], default: 'Equals' },
      value: { type: 'select', options: this.campaignStages }
    },
    {
      name: 'CAMPAIGN_NOTES',
      formControlName: 'campaign.campaignNotes',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CAMPAIGN_CREATION_NOTES',
      formControlName: 'campaign.campaignCreationNotes',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'TAGS',
      formControlName: 'campaign.tags',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CRM_NAME',
      formControlName: 'campaign.crmName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CRM_ID',
      formControlName: 'campaign.crmId',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'STUDIO_STATUS',
      formControlName: 'campaign.studioStatus',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    }
  ];

  clientsColumns = [
    {
      name: 'FIRST_NAME',
      formControlName: 'client.firstName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LAST_NAME',
      formControlName: 'client.lastName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'DMS',
      formControlName: 'client.dms',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'SALES_REP',
      formControlName: 'client.salesRep',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PHONE',
      formControlName: 'client.phone',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'ADDRESS',
      formControlName: 'client.address',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'TAGS',
      formControlName: 'client.tags',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'NOTES',
      formControlName: 'client.notes',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'EMAIL',
      formControlName: 'client.email',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    }
  ];

  companyColumns = [
    {
      name: 'COMPANY_NAME',
      formControlName: 'company.companyName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CONTACT_NAME',
      formControlName: 'company.contactName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CONTACT_EMAIL',
      formControlName: 'company.contactEmail',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CONTACT_PHONE',
      formControlName: 'company.contactPhone',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    }
  ];

  leadColumns = [
    {
      name: 'FIRST_NAME',
      formControlName: 'lead.firstName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'LAST_NAME',
      formControlName: 'lead.lastName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'EMAIL',
      formControlName: 'lead.email',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PHONE',
      formControlName: 'lead.phone',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'REFERRAL_ID',
      formControlName: 'lead.referralid',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'REFERRAL_SOURCE',
      formControlName: 'lead.referralsource',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CRM_NAME',
      formControlName: 'lead.crmName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CRM_ID',
      formControlName: 'lead.crmId',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PAGE_TITLE',
      formControlName: 'lead.pageTitle',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PAGE_URL',
      formControlName: 'lead.pageUrl',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'CONVERTED_TO_MEMBER',
      formControlName: 'lead.isMember',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }], default: 'Equals' },
      value: { type: 'select', options: [{ name: 'COMMON.YES', value: '1' }, { name: 'COMMON.NO', value: '0' }] }
    }
  ];

  tagColumns = [
    {
      name: 'TAG_NAME',
      formControlName: 'tag.tagName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'TAG_DESCRIPTION',
      formControlName: 'tag.tagDescription',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'TAG_CATEGORIES',
      formControlName: 'tag.tagCategories',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    }
  ];
  prospectColumns: any[] = [];

  priorityOptions = [{ name: 'ANY', value: '' }, ...TASK_PRIORITY.map(x => {
    const key = `TASK_PRIORITY.${(x.title ?? '').replace(/\s+/g, '_').replace(/-/g, '_').toUpperCase()}`;
    return { name: key, value: x.value.toString() };
  })];
  duedateStatusOptions: any[] = [
    { name: 'ALL', value: '' },
    { name: 'OVERDUE', value: 'Overdue' },
    { name: 'TODAY', value: 'Today' },
    { name: 'UPCOMING', value: 'Upcoming' }];

  completionStatusOptions: any[] = [
    { name: 'ALL', value: '' },
    { name: 'COMPLETED', value: 'Completed' },
    { name: 'NOT_COMPLETED', value: 'NotCompleted' }];

  completionDateOperations: any[] = [
    { name: 'ANYTIME', value: '' },
    { name: 'TODAY', value: 'Today' },
    { name: 'LAST_WEEK', value: 'LastWeek' },
    { name: 'LAST_MONTH', value: 'LastMonth' },
    { name: 'LAST_90_DAYS', value: 'Last90Days' },
    { name: 'REPORTING_PERIOD.CUSTOM', value: 'CustomPeriod' }];
  taskColumns = [
    {
      name: 'TITLE',
      formControlName: 'tasks.title',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'DESCRIPTION',
      formControlName: 'tasks.description',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'PRIORITY',
      formControlName: 'tasks.priority',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }, { name: 'OPERATORS.NOT_EQUALS', value: 'Not Equals' }], default: 'Equals' },
      value: { type: 'select', options: this.priorityOptions }
    },
    {
      name: 'DUE_DATE',
      formControlName: 'tasks.duedate',
      operator: {
        options: ['Equals', 'GreaterThan', 'GreaterThanOrEqual', 'LessThan', 'LessThanOrEqual'].map(x => {
          const dateOpKeyMap: any = {
            Equals: 'OPERATORS.EQUALS',
            GreaterThan: 'OPERATORS.GREATER_THAN',
            GreaterThanOrEqual: 'OPERATORS.GREATER_THAN_OR_EQUAL',
            LessThan: 'OPERATORS.LESS_THAN',
            LessThanOrEqual: 'OPERATORS.LESS_THAN_OR_EQUAL'
          };
          return { name: dateOpKeyMap[x] ?? x, value: x };
        }),
        default: 'Equals'
      },
      value: { type: 'datePicker', mode: 'single' }
    },
    {
      name: 'DUE_DATE_STATUS',
      formControlName: 'tasks.duedateStatus',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }], default: 'Equals' },
      value: { type: 'select', options: this.duedateStatusOptions }
    },
    {
      name: 'COMPLETION_STATUS',
      formControlName: 'tasks.completionStatus',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }], default: 'Equals' },
      value: { type: 'select', options: this.completionStatusOptions, }
    },
    {
      name: 'COMPLETED_WITHIN',
      formControlName: 'tasks.completionDate',
      operator: { options: this.completionDateOperations, default: '', change: (t: any, r: any) => this.onCompletionDateChange(t, r) },
      value: { type: 'datePicker', mode: 'range', clickOpens: false }
    },
    {
      name: 'ASSIGNED_USER',
      formControlName: 'tasks.assignedUser',
      operator: { options: [{ name: 'OPERATORS.EQUALS', value: 'Equals' }], default: 'Equals' },
      value: { type: 'select', options: [] }
    }
  ];

  userColumns = [
    {
      name: 'USER_NAME',
      formControlName: 'user.userName',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    },
    {
      name: 'USER_EMAIL',
      formControlName: 'user.userEmail',
      operator: { options: this.stringOperations, default: this.defaultStringOperation },
      value: { type: 'text' }
    }
  ];

  getColumnsByType(dataType: any) {
    if (dataType === 'campaign') return this.campaignColumns;
    if (dataType === 'client') return this.clientsColumns;
    if (dataType === 'company') return this.companyColumns;
    if (dataType === 'lead') return this.leadColumns;
    if (dataType === 'tag') return this.tagColumns;
    if (dataType === 'user') return this.userColumns;
    return null;
  }


  addNewColumn(dataType: any, newField: any) {
    var formControlName = newField.formControlName.split('_')[0];
    if (dataType === 'campaign') {
      var index = this.campaignColumns.findIndex(x => x.formControlName === formControlName);
      this.campaignColumns.splice(index + 1, 0, newField);
      this.campaignColumns = [...this.campaignColumns];
    }
    if (dataType === 'client') {
      var index = this.clientsColumns.findIndex(x => x.formControlName === formControlName);
      this.clientsColumns.splice(index + 1, 0, newField);
      this.clientsColumns = [...this.clientsColumns];
    }
    if (dataType === 'company') {
      var index = this.companyColumns.findIndex(x => x.formControlName === formControlName);
      this.companyColumns.splice(index + 1, 0, newField);
      this.companyColumns = [...this.companyColumns];
    }
    if (dataType === 'lead') {
      var index = this.leadColumns.findIndex(x => x.formControlName === formControlName);
      this.leadColumns.splice(index + 1, 0, newField);
      this.leadColumns = [...this.leadColumns];
    }
    if (dataType === 'tag') {
      var index = this.tagColumns.findIndex(x => x.formControlName === formControlName);
      this.tagColumns.splice(index + 1, 0, newField);
      this.tagColumns = [...this.tagColumns];
    }
    if (dataType === 'user') {
      var index = this.userColumns.findIndex(x => x.formControlName === formControlName);
      this.userColumns.splice(index + 1, 0, newField);
      this.userColumns = [...this.userColumns];
    }
    return null;
  }
}